import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { ArrowLeft, Edit } from "lucide-react"
import { RichTextDisplay } from "@/components/ui/rich-text-display"
import { formService } from "@/services/formService"
import { RegistrationForm } from "@/types/form-types"
import { formatDateHKT } from "@/utils/dateTimeHKT"
import { useAuth } from "@/contexts/auth-context"

export default function FormDetailPage() {
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const [form, setForm] = useState<RegistrationForm | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchForm = async () => {
      if (!id) return
      
      try {
        setLoading(true)
        const formData = await formService.getForm(id)
        setForm(formData)
      } catch (err) {
        console.error("Error fetching form:", err)
        setError("Failed to load form details")
      } finally {
        setLoading(false)
      }
    }

    fetchForm()
  }, [id])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (error || !form) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error || "Form not found"}</p>
          <Button asChild>
            <Link to="/manage/forms">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Forms
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button variant="outline" asChild className="mb-4">
          <Link to="/manage/forms">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Forms
          </Link>
        </Button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">{form.title}</h1>
            {form.description && (
              <div className="mt-2">
                <RichTextDisplay 
                  content={form.description} 
                  className="text-gray-600"
                  placeholder="No description available."
                />
              </div>
            )}
          </div>
          {user?.role === 'admin' && (
            <Button asChild>
              <Link to={`/manage/forms/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Form
              </Link>
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Form Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <Badge variant={form.isActive ? "default" : "secondary"}>
                    {form.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="mt-1">{form.category || "Uncategorized"}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1">{formatDateHKT(form.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Updated</label>
                <p className="mt-1">{formatDateHKT(form.updatedAt || form.createdAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Form Sections ({form.sections?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {form.sections && form.sections.length > 0 ? (
              <div className="space-y-4">
                {form.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="border rounded-lg p-4">
                    <h3 className="font-medium mb-2">{section.title}</h3>
                    {section.description && (
                      <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                    )}
                    {section.fields && section.fields.length > 0 ? (
                      <div className="space-y-2">
                        {section.fields.map((field, fieldIndex) => (
                          <div key={fieldIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{field.label}</span>
                                <span className="text-sm text-gray-500">({field.type})</span>
                                {field.required && (
                                  <Badge variant="outline" className="text-xs">Required</Badge>
                                )}
                              </div>
                              {field.placeholder && (
                                <p className="text-xs text-gray-500">Placeholder: "{field.placeholder}"</p>
                              )}
                                                  {field.helpText && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Help: </span>
                        <RichTextDisplay 
                          content={field.helpText} 
                          className="inline"
                        />
                      </div>
                    )}
                              {(field.type === "dropdown" || field.type === "radio" || field.type === "checkbox") && field.options && field.options.length > 0 && (
                                <div className="mt-1">
                                  <p className="text-xs text-gray-500 mb-1">Options:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {field.options.map((option, optionIndex) => (
                                      <Badge key={optionIndex} variant="secondary" className="text-xs">
                                        {option}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No fields in this section</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No sections defined for this form</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 